import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import EarlyBirdBadge from '../components/EarlyBirdBadge';
import ClimateBadgeCard from '../components/ClimateBadgeCard';
import {
  MapPin,
  Calendar,
  Users,
  ArrowUpRight,
  Ship,
  Anchor,
  Clock,
  Sparkles,
  Tag,
  ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { earlyBirdInfo } from '../lib/earlyBird';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Photos réelles des destinations
const destinationPhotos = {
  'tour-de-corse-croisiere-catamaran': 'https://static.wixstatic.com/media/ce6ce7_0ce032b8fe2e4bf58652c8d18e897478~mv2.jpg/v1/fill/w_900,h_500,al_c,q_80,enc_avif,quality_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO%20copie.jpg',
  'croisiere-catamaran-corse-du-sud': 'https://static.wixstatic.com/media/ce6ce7_98fe44ee0ba0419794505afe2c70aa95~mv2.jpg/v1/fill/w_900,h_500,al_c,q_80,enc_avif,quality_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO.jpg',
  'croisiere-catamaran-ouest-corse': 'https://static.wixstatic.com/media/ce6ce7_f3bee630d0c549388bbc71c0e58fb9ea~mv2.jpg/v1/fill/w_900,h_650,al_c,q_80,enc_avif,quality_auto/sognudimare%20a%20scandola_edited.jpg',
  'croisiere-catamaran-sardaigne-corse': 'https://static.wixstatic.com/media/ce6ce7_9e15d4c9779b4d48ad56a689ab2fe02b~mv2.avif/v1/fill/w_900,h_860,al_c,q_80,enc_avif,quality_auto/LAGOON%2043%20SOGNUDIMARE.avif',
  'croisiere-catamaran-grece-authentique': 'https://static.wixstatic.com/media/ce6ce7_227bd245ee9c48eca3a0f527080bf3a7~mv2.jpg/v1/fill/w_900,h_580,al_c,q_80,enc_avif,quality_auto/cephalonie%20grece.jpg',
  'croisiere-catamaran-grece-cyclades-2-semaines': '/images/grece_cyclades_hero.jpg',
  'croisiere-catamaran-grenadines-caraibes': 'https://static.wixstatic.com/media/ce6ce7_3eb608d9c85e4effadf3d95d064cac02~mv2.webp/v1/fill/w_900,h_600,al_c,q_80,enc_avif,quality_auto/grenadines-a-la-decouverte-des-grenadines_482185_panohd.webp',
};

const formatShort = (s) => {
  if (!s) return '';
  return new Date(s).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const countByYear = (dates, year) =>
  (dates || []).filter((d) => (d.start_date || '').startsWith(String(year))).length;

const nextDeparture = (dates, year) => {
  if (!dates) return null;
  const now = new Date();
  return dates
    .filter((d) => {
      if (year && !(d.start_date || '').startsWith(String(year))) return false;
      return new Date(d.start_date) > now && d.status !== 'full';
    })
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];
};

// ────────────────────────────────────────────────────────
// Early-bird master banner
// ────────────────────────────────────────────────────────
const EarlyBirdMasterBanner = () => (
  <div
    className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#0A2540] via-[#1D3557] to-[#0A2540] border border-[#C4724F]/30"
    data-testid="dest-early-bird-banner"
  >
    <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#C4724F]/20 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-[#2A9D8F]/15 rounded-full blur-3xl pointer-events-none" />
    <div className="relative px-5 py-6 sm:px-10 sm:py-8 flex flex-col lg:flex-row items-start lg:items-center gap-5 lg:gap-8">
      <div className="shrink-0">
        <div className="relative">
          <div className="absolute inset-0 bg-[#C4724F] rounded-2xl blur-md opacity-60 animate-pulse" />
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#C4724F] to-[#A0552F] rounded-2xl flex flex-col items-center justify-center text-white shadow-2xl rotate-[-4deg]">
            <Sparkles className="h-4 w-4 mb-0.5 opacity-90" />
            <div className="font-display text-3xl sm:text-4xl font-bold leading-none">−5%</div>
            <div className="text-[10px] uppercase tracking-wider mt-0.5 opacity-90">Tarif Anticipé</div>
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#2A9D8F]/20 border border-[#2A9D8F]/40 rounded-full mb-2">
          <Clock className="h-3 w-3 text-[#3AB8A8]" />
          <span className="text-[#3AB8A8] text-[10px] sm:text-xs font-semibold uppercase tracking-wider">
            Offre saison 2027
          </span>
        </div>
        <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mb-1.5">
          Réservez tôt, économisez <span className="italic text-[#C4956A]">−5 %</span>
        </h3>
        <p className="text-white/75 text-sm sm:text-base leading-relaxed">
          Bénéficiez de <span className="font-semibold text-white">5 % de remise</span> automatique
          pour toute réservation effectuée{' '}
          <span className="text-[#3AB8A8] font-semibold">au moins 10 mois avant le départ</span>.
          La remise s'applique en checkout — aucun code requis.
        </p>
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────
// Cruise card
// ────────────────────────────────────────────────────────
const CruiseCard = ({ cruise, showPrivatization }) => {
  const photoUrl = destinationPhotos[cruise.slug] || cruise.images?.[0];
  const count2027 = countByYear(cruise.dates, 2027);
  const next2027 = nextDeparture(cruise.dates, 2027);
  const earliestNext = next2027;

  // Discounted price preview based on the next eligible departure
  const showPrivatePrice =
    showPrivatization || (!cruise.is_cabin_booking && cruise.is_privatization);
  const basePrice = showPrivatePrice
    ? earliestNext?.privatization_price || cruise.privatization_base_price
    : earliestNext?.price_per_cabin || earliestNext?.cabin_price || cruise.base_price;
  const priceLabel = showPrivatePrice ? 'privatisation' : 'par passager';

  const eb = earliestNext ? earlyBirdInfo(earliestNext.start_date) : null;
  const discountedPrice =
    eb && eb.eligible && basePrice ? Math.round(basePrice * 0.95) : null;

  // Any departure currently eligible? (for top-right pill on photo)
  const hasEligibleDate = (cruise.dates || []).some(
    (d) => earlyBirdInfo(d.start_date).eligible
  );

  return (
    <article
      className="group relative bg-white rounded-3xl overflow-hidden border border-slate-200/60 shadow-[0_4px_20px_-8px_rgba(10,37,64,0.08)] hover:shadow-[0_24px_60px_-16px_rgba(10,37,64,0.22)] transition-all duration-500 flex flex-col"
      data-testid={`cruise-card-${cruise.slug}`}
    >
      {/* Photo */}
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={photoUrl}
          alt={cruise.destination}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/90 via-[#0A2540]/15 to-transparent" />

        {/* Top badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {cruise.is_on_request ? (
              <span className="px-2.5 py-1 bg-[#C4724F] text-white text-[10px] sm:text-xs font-semibold rounded-full shadow-sm">
                Sur demande
              </span>
            ) : (
              <>
                {cruise.is_cabin_booking && !showPrivatization && (
                  <span className="px-2.5 py-1 bg-[#2A9D8F] text-white text-[10px] sm:text-xs font-semibold rounded-full shadow-sm">
                    Cabine
                  </span>
                )}
                {cruise.is_privatization && (
                  <span className="px-2.5 py-1 bg-white/95 text-[#0A2540] text-[10px] sm:text-xs font-semibold rounded-full shadow-sm">
                    Privatisation
                  </span>
                )}
              </>
            )}
          </div>
          {hasEligibleDate && (
            <span className="shrink-0 px-2.5 py-1 bg-[#C4724F] text-white text-[10px] sm:text-xs font-bold rounded-full shadow-md flex items-center gap-1">
              <Tag className="h-3 w-3" />
              −5 % Tarif Anticipé
            </span>
          )}
        </div>

        {/* Bottom : destination + name */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 text-white">
          <div className="flex items-center gap-1.5 text-[#C4956A] text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] mb-1.5">
            <MapPin className="h-3 w-3" />
            {cruise.destination}
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight">
            {cruise.name}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 p-5 sm:p-6 flex flex-col">
        <p className="text-[#4A5568] text-sm leading-relaxed mb-4 line-clamp-2">
          {cruise.short_description || cruise.description}
        </p>

        {/* Mini info row */}
        <div className="grid grid-cols-3 gap-2 text-center pb-4 mb-4 border-b border-slate-100">
          <div>
            <Calendar className="h-4 w-4 text-[#2A9D8F] mx-auto mb-0.5" />
            <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Durée</div>
            <div className="text-xs font-semibold text-[#0A2540]">{cruise.duration}</div>
          </div>
          <div>
            <Ship className="h-4 w-4 text-[#2A9D8F] mx-auto mb-0.5" />
            <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Départ</div>
            <div className="text-xs font-semibold text-[#0A2540] truncate">
              {cruise.departure_port}
            </div>
          </div>
          <div>
            <Users className="h-4 w-4 text-[#2A9D8F] mx-auto mb-0.5" />
            <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Pax</div>
            <div className="text-xs font-semibold text-[#0A2540]">
              {cruise.max_passengers || 8} max
            </div>
          </div>
        </div>

        {/* Planning 2027 */}
        <div className="mb-5">
          <YearCell
            year="2027"
            count={count2027}
            next={next2027}
            color="terracotta"
            discount
          />
        </div>

        {/* Price + CTA */}
        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="min-w-0">
            {basePrice > 0 ? (
              <>
                <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">
                  À partir de
                </div>
                {discountedPrice ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-slate-400 line-through tabular-nums">
                      {basePrice.toLocaleString('fr-FR')}€
                    </span>
                    <span className="font-display text-2xl sm:text-3xl font-bold text-[#C4724F] leading-none tabular-nums">
                      {discountedPrice.toLocaleString('fr-FR')}€
                    </span>
                  </div>
                ) : (
                  <div className="font-display text-2xl sm:text-3xl font-bold text-[#0A2540] leading-none tabular-nums">
                    {Number(basePrice).toLocaleString('fr-FR')}€
                  </div>
                )}
                <div className="text-[10px] text-[#94A3B8] mt-0.5">{priceLabel}</div>
              </>
            ) : (
              <div className="text-sm text-[#94A3B8]">Sur devis</div>
            )}
          </div>
          <Link
            to={`/${cruise.slug}`}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 bg-[#0A2540] hover:bg-[#102E54] text-white text-sm font-semibold rounded-full shadow-md hover:shadow-xl transition-all group/btn"
            data-testid={`reserve-${cruise.slug}`}
          >
            Réserver
            <ArrowUpRight className="h-4 w-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </article>
  );
};

const YearCell = ({ year, count, next, color, discount }) => {
  const colors = {
    slate: { bg: 'bg-slate-50', border: 'border-slate-200', label: 'text-slate-500', year: 'text-[#0A2540]', count: 'text-[#2A9D8F]' },
    terracotta: { bg: 'bg-gradient-to-br from-[#FFF7F2] to-[#FFEDE0]', border: 'border-[#C4724F]/25', label: 'text-[#C4724F]', year: 'text-[#0A2540]', count: 'text-[#C4724F]' },
  };
  const c = colors[color] || colors.slate;
  const isEmpty = count === 0;
  return (
    <div className={`relative rounded-xl border ${c.border} ${c.bg} px-3 py-2.5 ${isEmpty ? 'opacity-50' : ''}`}>
      {discount && !isEmpty && (
        <span className="absolute -top-2 -right-1 px-1.5 py-0.5 bg-[#C4724F] text-white text-[9px] font-bold rounded-full shadow">
          −5 %
        </span>
      )}
      <div className="flex items-baseline justify-between gap-1.5">
        <span className={`font-display text-base sm:text-lg font-bold ${c.year} leading-none`}>
          {year}
        </span>
        <span className={`text-[10px] uppercase tracking-wider ${c.label}`}>Saison</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className={`text-lg sm:text-xl font-bold ${c.count} tabular-nums leading-none`}>
          {count}
        </span>
        <span className="text-[10px] text-slate-500">{count > 1 ? 'départs' : 'départ'}</span>
      </div>
      <div className="mt-0.5 text-[10px] text-slate-500 truncate">
        {next ? `Prochain : ${formatShort(next.start_date)}` : isEmpty ? 'Bientôt' : '—'}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────
// Filter chips
// ────────────────────────────────────────────────────────
const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'cabin', label: 'Cabine' },
  { id: 'privatization', label: 'Privatisation' },
  { id: 'corse', label: 'Corse' },
  { id: 'grece', label: 'Grèce' },
  { id: 'caraibes', label: 'Caraïbes' },
];

const FilterChips = ({ filter, setFilter }) => (
  <div className="flex gap-2 overflow-x-auto -mx-5 px-5 pb-1 scrollbar-hide" data-testid="dest-filters">
    {FILTERS.map((f) => (
      <button
        key={f.id}
        onClick={() => setFilter(f.id)}
        className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all border ${
          filter === f.id
            ? 'bg-[#0A2540] text-white border-[#0A2540] shadow-md'
            : 'bg-white text-[#0A2540] border-slate-200 hover:border-[#0A2540] hover:bg-slate-50'
        }`}
        data-testid={`filter-${f.id}`}
      >
        {f.label}
      </button>
    ))}
  </div>
);

// ────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────
const DestinationsPage = () => {
  const [cruises, setCruises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchCruises = async () => {
      try {
        const r = await axios.get(`${API}/cruises`);
        setCruises(r.data || []);
      } catch (e) {
        console.error('Error fetching cruises:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCruises();
  }, []);

  const showPrivatization = filter === 'privatization';

  const filtered = useMemo(() => {
    return cruises.filter((c) => {
      const dest = (c.destination || '').toLowerCase();
      switch (filter) {
        case 'all':
          return true;
        case 'cabin':
          return c.is_cabin_booking;
        case 'privatization':
          return c.is_privatization;
        case 'corse':
          return dest.includes('corse');
        case 'grece':
          return dest.includes('grèce') || dest.includes('grece') || dest.includes('cyclades');
        case 'caraibes':
          return dest.includes('caraïb') || dest.includes('caraib') || dest.includes('grenadines');
        default:
          return true;
      }
    });
  }, [cruises, filter]);

  // KPIs derived from data
  const kpis = useMemo(() => {
    const dates2027 = cruises.reduce((s, c) => s + countByYear(c.dates, 2027), 0);
    return { destinations: cruises.length, d2027: dates2027 };
  }, [cruises]);

  return (
    <Layout>
      <SEO
        title="Croisières catamaran 2027 — Corse, Sardaigne, Grèce, Caraïbes"
        description="7 itinéraires en catamaran avec équipage : Corse (Ajaccio, Porto-Vecchio, Calvi), Sardaigne, Grèce, Cyclades, Caraïbes. Planning 2027. Cabine ou privatisation. Remise −5 % Tarif Anticipé."
        path="/destinations-croisieres-catamaran"
        keywords={[
          'croisière catamaran corse',
          'croisière catamaran ajaccio',
          'croisière catamaran porto-vecchio',
          'croisière catamaran calvi',
          'croisière catamaran sardaigne',
          'croisière catamaran grèce',
          'croisière catamaran cyclades',
          'croisière catamaran caraïbes',
          'croisière catamaran grenadines',
          'tour de corse en catamaran',
          'planning croisière 2027',
          'planning croisière 2027',
          'cabine catamaran corse',
          'privatisation catamaran corse',
          'lagoon 46 corse',
          'lagoon 43 corse',
        ]}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Destinations — Croisières catamaran SOGNUDIMARE',
            url: 'https://sognudimare.com/destinations-croisieres-catamaran',
            inLanguage: 'fr-FR',
            isPartOf: { '@type': 'WebSite', name: 'SOGNUDIMARE', url: 'https://sognudimare.com' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://sognudimare.com/' },
              { '@type': 'ListItem', position: 2, name: 'Destinations', item: 'https://sognudimare.com/destinations-croisieres-catamaran' },
            ],
          },
        ]}
      />

      {/* ── Hero éditorial ── */}
      <section
        className="relative pt-28 sm:pt-32 pb-12 sm:pb-16 bg-[#0A1E37] overflow-hidden"
        data-testid="destinations-hero"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[600px] h-[400px] bg-[#2A9D8F]/15 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-[#C4724F]/15 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 xl:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
                <Anchor className="h-3.5 w-3.5 text-[#3AB8A8]" />
                <span className="text-white/80 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em]">
                  7 destinations · Planning 2027
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[0.98] tracking-tight mb-5">
                Nos <span className="italic text-[#2A9D8F]">croisières</span>
                <br />
                en catamaran
              </h1>
              <p className="text-white/75 text-base sm:text-lg max-w-2xl leading-relaxed">
                Réservation à la cabine ou privatisation complète, en pension complète avec équipage
                professionnel. Planning 2027 disponible.
              </p>

              {/* KPI strip */}
              <div className="mt-7 sm:mt-9 grid grid-cols-2 gap-3 max-w-md">
                <Kpi value={kpis.destinations} label="Destinations" />
                <Kpi value={kpis.d2027} label="Départs 2027" highlight />
              </div>
            </div>

            {/* Climate commitment card (right column) */}
            <div className="lg:col-span-5 xl:col-span-4">
              <ClimateBadgeCard />
            </div>
          </div>
        </div>
      </section>

      {/* ── List ── */}
      <section
        className="relative py-12 sm:py-16 bg-gradient-to-b from-[#FAFAF7] via-[#F5F3EF] to-[#FAFAF7] overflow-hidden"
        data-testid="cruises-list"
      >
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          {/* Banner */}
          <div className="mb-8 sm:mb-10">
            <EarlyBirdMasterBanner />
          </div>

          {/* Filters */}
          <div className="mb-7 sm:mb-9">
            <FilterChips filter={filter} setFilter={setFilter} />
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-[520px] bg-white rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200">
              <p className="text-[#4A5568]">Aucune croisière ne correspond à ce filtre.</p>
              <button
                onClick={() => setFilter('all')}
                className="mt-3 text-[#C4724F] font-semibold underline-offset-4 hover:underline"
              >
                Réinitialiser
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {filtered.map((cruise) => (
                <CruiseCard
                  key={cruise.slug || cruise.id}
                  cruise={cruise}
                  showPrivatization={showPrivatization}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Trust strip ── */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            <TrustCard
              icon={Ship}
              title="Catamarans récents"
              text="Naviguez sur des bateaux confortables et parfaitement équipés."
              accent="#2A9D8F"
            />
            <TrustCard
              icon={Users}
              title="Équipage professionnel"
              text="Capitaine expérimenté et hôtesse aux petits soins."
              accent="#C4724F"
            />
            <TrustCard
              icon={Calendar}
              title="Dates flexibles"
              text="Planning 2027 disponible toute l'année."
              accent="#2A9D8F"
            />
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14 sm:py-20 bg-[#0A1E37] relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-[#2A9D8F]/15 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Notre équipe est là pour vous conseiller et trouver la croisière parfaite,
            ou construire un programme sur mesure rien que pour vous.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C4724F] hover:bg-[#B0613F] text-white font-semibold text-sm sm:text-base shadow-xl shadow-[#C4724F]/30 transition-all hover:scale-[1.02]"
              data-testid="cta-devis"
            >
              Demander un devis personnalisé
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:0495729028"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/30 text-white font-semibold text-sm sm:text-base hover:bg-white/10 backdrop-blur-sm transition-all"
              data-testid="cta-appeler"
            >
              04 95 72 90 28
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const Kpi = ({ value, label, highlight }) => (
  <div
    className={`px-3 py-3 sm:px-4 sm:py-4 rounded-2xl border backdrop-blur-md ${
      highlight
        ? 'bg-[#C4724F]/15 border-[#C4724F]/40'
        : 'bg-white/5 border-white/15'
    }`}
  >
    <div
      className={`font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-none mb-1 ${
        highlight ? 'text-[#C4956A]' : 'text-white'
      }`}
    >
      {value}
    </div>
    <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/60">
      {label}
    </div>
  </div>
);

const TrustCard = ({ icon: Icon, title, text, accent }) => (
  <div className="group p-6 sm:p-7 rounded-3xl border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all bg-gradient-to-br from-white to-slate-50/50">
    <div
      className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shadow-md group-hover:scale-105 transition-transform"
      style={{ backgroundColor: accent }}
    >
      <Icon className="h-6 w-6 text-white" />
    </div>
    <h3 className="font-display text-lg sm:text-xl font-bold text-[#0A2540] mb-2">{title}</h3>
    <p className="text-[#4A5568] text-sm leading-relaxed">{text}</p>
  </div>
);

export default DestinationsPage;
